// Question Types and Enums (Frontend)

// Emoji types for sentiment-based feedback (like airport feedback terminals)
export type EmojiType =
  | 'very_satisfied'
  | 'satisfied'
  | 'neutral'
  | 'dissatisfied'
  | 'very_dissatisfied';

export enum QuestionType {
  YES_NO = 'YES_NO',
  SINGLE_SELECT = 'SINGLE_SELECT',
  MULTI_SELECT = 'MULTI_SELECT',
  TEXT = 'TEXT',
  NUMBER = 'NUMBER',
  RANGE = 'RANGE',
}

// Question Option Interface for Select Questions
export interface QuestionOption {
  id: string;
  label: Record<string, string>; // Multi-language support
  value: string;
  order: number;
  color?: string; // Optional hex color for chart visualization
  emoji?: EmojiType; // Optional sentiment emoji for visual feedback
}

// Range Configuration for Range/Rating Questions
export interface RangeConfiguration {
  min: number;
  max: number;
  step?: number;
  labels?: {
    min?: Record<string, string>; // Multi-language
    max?: Record<string, string>; // Multi-language
    intermediate?: Record<number, Record<string, string>>; // Multi-language labels for specific values
  };
  displayType: 'slider' | 'stars' | 'buttons' | 'scale';
}

// Validation Rules for Input Questions
export interface ValidationRules {
  minLength?: number;
  maxLength?: number;
  pattern?: string; // Regex pattern
  minValue?: number; // For numeric inputs
  maxValue?: number; // For numeric inputs
  required?: boolean; // Redundant with isRequired but kept for consistency
}

// Question Creation Data Interface
export interface QuestionCreationData {
  type: QuestionType;
  title: Record<string, string>;
  description?: Record<string, string>;
  order?: number; // Optional - will be auto-assigned if not provided
  isRequired?: boolean;
  hasCommentField?: boolean;
  commentFieldDescription?: Record<string, string>;
  options?: Omit<QuestionOption, 'id'>[]; // Options without ID (will be generated)
  rangeConfig?: RangeConfiguration;
  validationRules?: ValidationRules;
}

// Question Update Data Interface
export interface QuestionUpdateData {
  type?: QuestionType;
  title?: Record<string, string>;
  description?: Record<string, string>;
  order?: number;
  isRequired?: boolean;
  hasCommentField?: boolean;
  commentFieldDescription?: Record<string, string>;
  options?: QuestionOption[]; // Full options with IDs for updates
  rangeConfig?: RangeConfiguration;
  validationRules?: ValidationRules;
}

// Question Filter Interface for Repository Queries
export interface QuestionFilters {
  type?: QuestionType;
  isRequired?: boolean;
  hasCommentField?: boolean;
  contextType?: 'SURVEY' | 'TEMPLATE';
}

// Question Sort Options
export interface QuestionSortOptions {
  field: 'order' | 'title' | 'createdAt' | 'updatedAt';
  direction: 'asc' | 'desc';
}

// Question Pagination Interface
export interface QuestionPaginationOptions {
  limit?: number;
  offset?: number;
  lastEvaluatedKey?: Record<string, unknown>;
}

export interface LanguageTabConfig {
  code: string;
  label: string;
  isDefault?: boolean;
}

// Available languages for multi-language support
export const SUPPORTED_LANGUAGES: LanguageTabConfig[] = [
  { code: 'no', label: 'Norsk', isDefault: true },
  { code: 'en', label: 'English' },
];

// Question type display configuration
export interface QuestionTypeConfig {
  type: QuestionType;
  label: string;
  description: string;
  icon: string;
  requiresOptions: boolean;
  requiresRangeConfig: boolean;
  supportsValidation: boolean;
}

export const QUESTION_TYPE_CONFIGS: QuestionTypeConfig[] = [
  {
    type: QuestionType.YES_NO,
    label: 'Yes/No',
    description: 'Simple yes or no question',
    icon: 'CheckBox',
    requiresOptions: false,
    requiresRangeConfig: false,
    supportsValidation: false,
  },
  {
    type: QuestionType.SINGLE_SELECT,
    label: 'Single Choice',
    description: 'Choose one option from a list',
    icon: 'RadioButtonChecked',
    requiresOptions: true,
    requiresRangeConfig: false,
    supportsValidation: false,
  },
  {
    type: QuestionType.MULTI_SELECT,
    label: 'Multiple Choice',
    description: 'Choose multiple options from a list',
    icon: 'CheckBox',
    requiresOptions: true,
    requiresRangeConfig: false,
    supportsValidation: false,
  },
  {
    type: QuestionType.TEXT,
    label: 'Text',
    description: 'Short or long text input',
    icon: 'TextFields',
    requiresOptions: false,
    requiresRangeConfig: false,
    supportsValidation: true,
  },
  {
    type: QuestionType.NUMBER,
    label: 'Number',
    description: 'Numeric input with validation',
    icon: 'Numbers',
    requiresOptions: false,
    requiresRangeConfig: false,
    supportsValidation: true,
  },
  {
    type: QuestionType.RANGE,
    label: 'Rating Scale',
    description: 'Star ratings, scales, or sliders',
    icon: 'Star',
    requiresOptions: false,
    requiresRangeConfig: true,
    supportsValidation: false,
  },
];
